/**
 * Did an edit change the app, or replace it?
 *
 * "Tool Library Borrow" — a tool-library borrowing sheet — was given the instruction "add a
 * field for the borrower phone number" and came back as a personal Lending Tracker: a
 * different app, for a different person, which then asked whether they had wanted a rental
 * system, a library, or a loan/finance app. One field became a new product. The owner's app
 * was gone and nothing reported a problem, because every individual thing about the new app
 * was fine.
 *
 * It also reports the case where the edit would strand saved data — a collection dropped, or
 * moved to another kind of storage — and the case where the data survives and the APP does
 * not. Reported, never refused: the /edit handler returns these beside the new app, because
 * only the caller knows whether the person asked for something that sweeping.
 *
 * WHAT IT IS AND IS NOT. Whether an app still reads as the same product is a judgement, and
 * this does not make it. It measures the mechanical part — the screens, the collections, the
 * headings the app calls itself — which is the part that changed beyond argument when a
 * borrowing sheet became a tracker. A person still has to look at the rest.
 *
 * MEASURED BEFORE IT WAS WRITTEN, over the seven-app corpus:
 *   each app against itself           7/7 no drift
 *   each app against a realistic edit 4/4 no drift  (one <input> added to a form)
 *   each app against a DIFFERENT app  6/6 drift
 * No false positives, and it fires on every substitution — the two things it had to be. A
 * check that fired on a real edit would block the feature; one that missed a substitution
 * would have let Tool Library through.
 */

/** The screens an app has. `S.go(id)` switches between these, so they are its structure. */
export function screenIds(appCode: string): Set<string> {
  return new Set([...appCode.matchAll(/data-screen=["']([^"']+)["']/g)].map((m) => m[1]));
}

/**
 * Every collection the app reads or writes, local or cloud — and the lists the OWNER manages.
 *
 * `S.list` counts. It is how owner-managed rows work (the courses, shifts or menu an owner keeps
 * in Manage), and it was left out: an edit that renamed `S.list('courses', …)` reported no drift,
 * and an undo that took `S.list('courses')` back out of the app warned about nothing — though
 * the rows the owner had entered were exactly what it stranded. (planCoverage, which asks where
 * an app COLLECTS, reads `collectionKinds` and leaves lists out: nobody collects into one.)
 *
 * The name is anything up to the closing quote, deliberately. The first version required
 * `[A-Za-z][A-Za-z0-9_]*`, which silently ignored every HYPHENATED name — and a real
 * generated app called its notes board `shed-notes`, so this function reported the app as
 * having no collections of that kind at all. Worse than a miscount: `subjectDrift` uses it
 * to decide whether an edit stranded somebody's data, so a dropped `shed-notes` would have
 * been invisible to the guard whose entire job is noticing that.
 *
 * The gate's own extractors avoid this by PARSING and reading the literal's value, hyphens
 * and all. This one stays a regex because it must work on a `before` and `after` pair where
 * either may not parse — but the character class had no business being narrow.
 */
export function collectionNames(appCode: string): Set<string> {
  return new Set(
    [...appCode.matchAll(/S\.(?:store|submit|cloud|list)\(\s*(['"])([^'"]+)\1/g)].map((m) => m[2]),
  );
}

/**
 * Every collection with the kinds of storage it is read or written through.
 *
 * `collectionNames` alone cannot see a collection MOVE. An edit that turns
 * `S.store('rounds')` into `S.cloud('rounds')` keeps the name, so it looked like no change
 * at all — yet the app now reads an empty account while every round a visitor saved stays
 * in their browser, where the new app never looks. The prompt that used to be sent with
 * every edit asked for exactly that move ("S.cloud … never S.store"), on edits about
 * nothing of the kind.
 */
export function collectionKinds(appCode: string): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const m of appCode.matchAll(/S\.(store|submit|cloud|list)\(\s*(['"])([^'"]+)\2/g)) {
    const kinds = out.get(m[3]) ?? new Set<string>();
    kinds.add(m[1]);
    out.set(m[3], kinds);
  }
  return out;
}

/**
 * What the app calls itself on screen.
 *
 * Lower-cased and trimmed because an edit may legitimately re-case or re-space a heading;
 * what matters is whether the app is still talking about the same thing.
 */
export function headingTexts(appCode: string): string[] {
  return [...appCode.matchAll(/<h1[^>]*>([^<]{2,60})<\/h1>/g)].map((m) => m[1].trim().toLowerCase());
}

export interface Drift {
  /** Screens the edit removed. A screen is a thing the owner could previously reach. */
  droppedScreens: string[];
  /** Collections the edit removed — somebody's saved rows, left where nothing reads them. */
  droppedCollections: string[];
  /** Collections still named but now kept somewhere else — `S.store` to `S.cloud`, say. The
   *  same stranding as a drop, with the name hiding it. */
  movedCollections: { collection: string; from: string; to: string }[];
  /** How many of the app's own headings survived, out of how many it had. */
  keptHeadings: number;
  totalHeadings: number;
  /** Plain-English reasons this looks like a replacement rather than an edit, or strands
   *  somebody's saved rows. */
  reasons: string[];
}

export function subjectDrift(before: string, after: string): Drift {
  const screensBefore = screenIds(before);
  const screensAfter = screenIds(after);
  const droppedScreens = [...screensBefore].filter((s) => !screensAfter.has(s));

  const collectionsBefore = collectionNames(before);
  const collectionsAfter = collectionNames(after);
  const droppedCollections = [...collectionsBefore].filter((c) => !collectionsAfter.has(c));

  const kindsAfter = collectionKinds(after);
  const movedCollections: Drift['movedCollections'] = [];
  for (const [collection, kinds] of collectionKinds(before)) {
    const now = kindsAfter.get(collection);
    if (!now) continue; // dropped outright — reported as such
    const left = [...kinds].filter((k) => !now.has(k));
    if (left.length) {
      movedCollections.push({
        collection,
        from: left.map((k) => `S.${k}`).join(', '),
        to: [...now].map((k) => `S.${k}`).join(', '),
      });
    }
  }

  const headingsBefore = headingTexts(before);
  const headingsAfter = headingTexts(after);
  const keptHeadings = headingsBefore.filter((h) => headingsAfter.includes(h)).length;

  const reasons: string[] = [];

  /*
   * Every heading gone AND a screen gone. Either alone is an ordinary edit — rewording a
   * title is a heading change, and removing a screen someone asked to remove is a screen
   * change. Together they mean the app is talking about something else in a different
   * shape, which is exactly what Tool Library → Lending Tracker looked like.
   */
  if (headingsBefore.length > 0 && keptHeadings === 0 && droppedScreens.length > 0) {
    reasons.push(
      `none of the app's ${headingsBefore.length} heading(s) survived and ${droppedScreens.length} screen(s) were removed — this reads as a different app rather than an edited one`,
    );
  }

  /*
   * More than half the screens gone. A small instruction does not remove half an app, and
   * an instruction that legitimately does (a person asking to cut features) is worth
   * confirming rather than assuming.
   */
  if (screensBefore.size >= 3 && droppedScreens.length * 2 > screensBefore.size) {
    reasons.push(
      `${droppedScreens.length} of ${screensBefore.size} screens were removed (${droppedScreens.join(', ')})`,
    );
  }

  // Named separately from the screen count because the consequence is different: a dropped
  // collection is somebody's saved rows, not just a page they can no longer reach.
  if (droppedCollections.length) {
    reasons.push(`the collection(s) ${droppedCollections.join(', ')} are gone, so anything saved in them is stranded`);
  }
  // The same loss with the name hiding it: see `collectionKinds`.
  if (movedCollections.length) reasons.push(movedReason(movedCollections));

  return {
    droppedScreens,
    droppedCollections,
    movedCollections,
    keptHeadings,
    totalHeadings: headingsBefore.length,
    reasons,
  };
}

function movedReason(moved: Drift['movedCollections']): string {
  return `the collection(s) ${moved.map((m) => `${m.collection} (${m.from} → ${m.to})`).join(', ')} are now kept somewhere else, so anything already saved in the old place is stranded`;
}

/**
 * Shorthand for a caller that only wants to know whether to stop.
 *
 * A MOVE alone does not count. The build's own plan repair asks for one — `planCoverage`
 * turns `S.store('shed-notes')` into `S.cloud('shed-notes')` — and before anybody has used
 * the app it strands nothing, so treating it as a replacement would throw away exactly the
 * repair that was asked for. The edit path, where people's rows already exist, reads
 * `reasons`, which does carry it.
 */
export function looksLikeADifferentApp(before: string, after: string): boolean {
  const drift = subjectDrift(before, after);
  const moved = drift.movedCollections.length ? movedReason(drift.movedCollections) : null;
  return drift.reasons.some((r) => r !== moved);
}
